import React from 'react';
import {
  Package,
  Users,
  ShoppingCart,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Eye,
  Star,
  Clock,
  CheckCircle
} from 'lucide-react';
import SimpleChart from './SimpleChart';

const DashboardOverview = ({ data }) => {
  const stats = [
    {
      title: 'Total Products',
      value: data.totalProducts,
      change: '+12%',
      changeType: 'increase',
      icon: Package,
      color: 'blue'
    },
    {
      title: 'Total Users',
      value: data.totalUsers,
      change: '+8%',
      changeType: 'increase',
      icon: Users,
      color: 'green'
    },
    {
      title: 'Total Orders',
      value: data.totalOrders,
      change: '+23%',
      changeType: 'increase',
      icon: ShoppingCart,
      color: 'purple'
    },
    {
      title: 'Revenue',
      value: `$${data.revenue.toLocaleString()}`,
      change: '+15%',
      changeType: 'increase',
      icon: DollarSign,
      color: 'yellow'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200',
      yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="text-gray-600">Welcome back! Here's what's happening with your store.</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center">
            <Eye size={16} className="mr-2" />
            View Store
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          const isIncrease = stat.changeType === 'increase';
          
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 rounded-lg border ${getColorClasses(stat.color)}`}>
                  <Icon size={24} />
                </div>
              </div>
              <div className="flex items-center mt-4">
                {isIncrease ? (
                  <TrendingUp size={16} className="text-green-500 mr-1" />
                ) : (
                  <TrendingDown size={16} className="text-red-500 mr-1" />
                )}
                <span className={`text-sm font-medium ${
                  isIncrease ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stat.change}
                </span>
                <span className="text-sm text-gray-500 ml-1">from last month</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Sales Overview</h3>
            <select className="text-sm border border-gray-300 rounded px-3 py-1">
              <option>Last 6 months</option>
              <option>Last year</option>
            </select>
          </div>
          <SimpleChart data={data.salesData} type="line" />
        </div>

        {/* Category Distribution */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Product Categories</h3>
            <button className="text-sm text-blue-600 hover:text-blue-700">View All</button>
          </div>
          <SimpleChart data={data.categoryData} type="doughnut" />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Orders</h3>
            <button className="text-sm text-blue-600 hover:text-blue-700">View All</button>
          </div>
          <div className="space-y-4">
            {data.recentOrders.map((order) => (
              <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <ShoppingCart size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{order.customer}</p>
                    <p className="text-sm text-gray-500">Order #{order.id}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900">${order.amount}</p>
                  <div className="flex items-center">
                    {order.status === 'completed' ? (
                      <CheckCircle size={14} className="text-green-500 mr-1" />
                    ) : (
                      <Clock size={14} className="text-yellow-500 mr-1" />
                    )}
                    <span className={`text-xs capitalize ${
                      order.status === 'completed' ? 'text-green-600' : 'text-yellow-600'
                    }`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 gap-3">
            <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left">
              <Package size={20} className="text-blue-600 mb-2" />
              <p className="font-medium text-gray-900">Add Product</p>
              <p className="text-xs text-gray-500">Create new product</p>
            </button>
            <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left">
              <Users size={20} className="text-green-600 mb-2" />
              <p className="font-medium text-gray-900">Manage Users</p>
              <p className="text-xs text-gray-500">View all users</p>
            </button>
            <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left">
              <Star size={20} className="text-yellow-600 mb-2" />
              <p className="font-medium text-gray-900">Featured Items</p>
              <p className="text-xs text-gray-500">Manage featured</p>
            </button>
            <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-left">
              <Eye size={20} className="text-purple-600 mb-2" />
              <p className="font-medium text-gray-900">Site Preview</p>
              <p className="text-xs text-gray-500">View live site</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverview;
